// Measures a box so the chrome can scale with it.
//
// Every chrome dimension is expressed in `u` = 1% of the media width,
// which is how the real UI behaves: Instagram's rail, caption and CTA
// keep the same proportions whether you're on an SE or a Pro Max. That
// makes the overlay resolution-independent, but it does mean we need
// the rendered pixel size — hence the observer rather than a fixed
// number passed down by each caller.
//
// Height matters too: a feed card in a fixed-height grid tile is
// rendered at natural proportions and then scaled to fit, which needs
// both the tile's height and the card's natural height.

import { useCallback, useEffect, useRef, useState } from 'react';

export type Size = { width: number; height: number };

export function useElementSize<T extends HTMLElement>(): [
  (node: T | null) => void,
  Size
] {
  const [size, setSize] = useState<Size>({ width: 0, height: 0 });
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    if (!node) return;

    // Seed synchronously so the first paint isn't a zero-size frame
    // (which would collapse every u-scaled dimension to 0 and flash).
    //
    // offsetWidth/Height, not getBoundingClientRect: the compact feed
    // card is measured from inside a scaled wrapper, and the rect is
    // transform-aware. Feeding a scaled height back into the scale
    // calculation would shrink the card a little more on every
    // remeasure. The offset box is the untransformed layout box, which
    // is also what ResizeObserver reports below — so both paths agree.
    setSize({ width: node.offsetWidth, height: node.offsetHeight });

    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const box = Array.isArray(entry.borderBoxSize) && entry.borderBoxSize.length
        ? { width: entry.borderBoxSize[0].inlineSize, height: entry.borderBoxSize[0].blockSize }
        : { width: entry.contentRect.width, height: entry.contentRect.height };
      setSize(prev => (
        Math.abs(prev.width - box.width) < 0.5 && Math.abs(prev.height - box.height) < 0.5
          ? prev
          : box
      ));
    });
    ro.observe(node);
    observerRef.current = ro;
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  return [ref, size];
}
